// Engagement Overview (Workspace 2, screen 3): the header (client, type, year end, team, stage, report due), the eight phase
// cards 00–07 (state, progress, one-line status; the current phase highlighted) with a line each for 08 client documents and
// 09 sign-offs, then "What's next" (who each item waits on), "Key dates", and "Open and closed" (what is settled, by whom). Engine internals sit under "Engine details".
import { Link } from "react-router-dom";
import { useApp } from "../../app/context";
import { Icon, LoadingRows, StatusPill } from "../../components";
import { fmtDate, titleCase } from "../../lib/format";
import { PHASES, age, nextItems, phaseSummary, reportDeadline, stageColumn, typeLabel, workPhase } from "../../lib/workspace";
import { DeadlineLine, daysText } from "../engagement/overviewParts";
import { useEngagement } from "../engagement/Layout";
import { EngineDetails, ItemAction, ProgressBar, Who, useWs, useWsAct } from "./common";
import { FlaggedIssuesSummary } from "./Issues";
import { StatusBoard } from "./StatusBoard";

const PHASE_STATE_LABEL: Record<string, string> = { done: "All final", blocked: "Needs input", in_progress: "In progress", not_started: "Not started", not_applicable: "Nothing here" };

export function WsOverview() {
  const ws = useWs();
  const { model } = useEngagement();
  const { me } = useApp();
  const { open, drawer } = useWsAct(ws.reload);
  const h = ws.header;
  const col = stageColumn(h);
  const now = workPhase(ws.wp);                                          // the card marked "Now" follows the work
  const due = reportDeadline(h);
  const staff = model.team.filter((t) => t.role === "staff").map((t) => t.person);
  const partner = h.partner ?? model.team.find((t) => t.role === "engagement_partner")?.person ?? null;
  const manager = h.manager ?? model.team.find((t) => t.role === "manager")?.person ?? null;
  const next = nextItems(ws.attention, me.personName);
  const docs = ws.pbc;
  const docsIn = docs.filter((d) => ["received", "processed", "issue"].includes(d.status)).length;
  const docsLate = docs.filter((d) => d.status === "late").length;
  const signRows = ws.wp.filter((w) => w.phase === "09");
  const upcoming = docs.filter((d) => d.due && ["outstanding", "late"].includes(d.status)).sort((a, b) => a.due!.localeCompare(b.due!)).slice(0, 3);

  return (
    <div className="space-y-5">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold">{h.clientName}</h1>
          <StatusPill kind={col === "Issued" ? "done" : "progress"} label={col} />
          {due?.due && <StatusPill state={due.status} label={`Report due ${fmtDate(due.due)}${daysText(due) ? ` · ${daysText(due)}` : ""}`} />}
        </div>
        <p className="text-sm text-muted">{typeLabel(h.engagementType)} · year ended {fmtDate(h.fye)}</p>
        <dl className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <div><dt className="inline text-muted">Partner </dt><dd className="inline">{partner ?? "—"}</dd></div>
          <div><dt className="inline text-muted">Manager </dt><dd className="inline">{manager ?? "—"}</dd></div>
          <div><dt className="inline text-muted">Staff </dt><dd className="inline">{staff.join(", ") || "—"}</dd></div>
        </dl>
      </header>

      <section aria-labelledby="phases-h" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="phases-h" className="text-sm font-semibold">Phases</h2>
          {h.progress && <div className="w-56"><ProgressBar done={h.progress.final} total={h.progress.total} label="Workpapers final, whole engagement" /></div>}
        </div>
        {ws.loading ? <LoadingRows rows={4} label="Loading the phases" /> : <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {PHASES.filter((p) => Number(p.code) <= 7).map((p) => {
            const rows = ws.wp.filter((w) => w.phase === p.code);
            const s = phaseSummary(rows);
            const current = now === p.code;
            return (
              <li key={p.code}>
                <Link to={`/e/${ws.id}/folders/${p.code}`} aria-current={current ? "step" : undefined}
                  className={`block h-full space-y-2 rounded-lg border p-3 hover:border-brand ${current ? "border-brand bg-brand/5 ring-1 ring-brand" : "border-line bg-raised"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold"><span className="font-mono text-muted">{p.code}</span> {p.name}</p>
                    {current && <span className="shrink-0 rounded bg-brand px-1.5 py-0.5 text-[10px] font-semibold uppercase text-brand-ink">Now</span>}
                  </div>
                  <StatusPill state={s.state} label={PHASE_STATE_LABEL[s.state]} />
                  {s.total > 0 && <ProgressBar done={s.final} total={s.total} label={`${p.name}: workpapers final`} />}
                  <p className="text-xs text-muted">{s.total ? `${s.final} of ${s.total} final${s.waitClient ? ` · ${s.waitClient} wait on client` : ""}${s.needs && !s.waitClient ? ` · ${s.needs} need input` : ""}` : "No workpapers expected here"}</p>
                </Link>
              </li>
            );
          })}
        </ul>}
        <div className="grid gap-2 sm:grid-cols-2">
          <Link to={`/e/${ws.id}/documents`} className="flex items-center gap-2 rounded-lg border border-line bg-raised px-3 py-2 text-sm hover:border-brand">
            <Icon name="clip" size={16} className="text-client" /><span className="font-mono text-muted">08</span><span className="font-medium">Client documents</span>
            <span className="ml-auto text-xs text-muted">{docsIn} of {docs.length} received{docsLate ? ` · ${docsLate} late` : ""}</span>
          </Link>
          <Link to={`/e/${ws.id}/folders/09`} className="flex items-center gap-2 rounded-lg border border-line bg-raised px-3 py-2 text-sm hover:border-brand">
            <Icon name="check" size={16} className="text-done" /><span className="font-mono text-muted">09</span><span className="font-medium">Sign-offs</span>
            <span className="ml-auto text-xs text-muted">{signRows.filter((w) => w.status === "final").length} of {signRows.length} final</span>
          </Link>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-3">
        <section aria-labelledby="next-h" className="rounded-lg border border-line bg-raised lg:col-span-2">
          <header className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <h2 id="next-h" className="text-sm font-semibold">What's next</h2>
            <Link to={`/e/${ws.id}/actions`} className="text-xs text-brand hover:underline">Action center</Link>
          </header>
          {next.length === 0 ? <p className="px-4 py-6 text-center text-sm text-muted">Nothing waits on anyone right now.</p> : (
            <ul className="divide-y divide-line">
              {next.map((i) => (
                <li key={i.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm">
                  <span className="min-w-0 flex-1">{i.title}{i.ref && <Link className="ml-1.5 font-mono text-xs text-brand hover:underline" to={`/e/${ws.id}/wp/${encodeURIComponent(i.ref)}`}>{i.ref}</Link>}</span>
                  <span className="text-xs">{i.waitsOn.kind === "client" ? <span className="text-client">Client</span> : i.waitsOn.kind === "agent" ? <Who name="agent" /> : <Who name={i.waitsOn.name} />}</span>
                  <span className="w-14 text-right text-xs text-muted">{age(i.createdAt)}</span>
                  <ItemAction item={i} open={open} />
                </li>
              ))}
            </ul>
          )}
        </section>
        <section aria-labelledby="dates-h" className="space-y-2 rounded-lg border border-line bg-raised px-4 py-3">
          <h2 id="dates-h" className="text-sm font-semibold">Key dates</h2>
          <ul className="space-y-1.5 text-sm" aria-label="Deadlines">
            <li><span className="text-muted">Year end</span> {fmtDate(h.fye)}</li>
            {h.deadlines.map((d, i) => <li key={i}><DeadlineLine d={d} /></li>)}
            {!h.deadlines.length && <li className="text-muted">No deadline recorded yet.</li>}
            {upcoming.map((d) => <li key={d.id}><span className="text-muted">{d.ref} due</span> {fmtDate(d.due)} <span className="text-xs text-muted">{d.title}</span></li>)}
          </ul>
        </section>
      </div>

      <FlaggedIssuesSummary engagementId={ws.id} />

      <StatusBoard id={ws.id} items={model.openItems} pbc={ws.pbc} />

      <EngineDetails>
        <p>Phase {titleCase(h.phase)} · record head <span className="font-mono">{h.auditHead ? `${h.auditHead.slice(0, 12)}…` : "—"}</span>{h.auditSeq !== null ? ` · seq ${h.auditSeq}` : ""}
          {h.lease ? ` · lease: ${h.lease.holderName}` : " · no engine holds the lease"}.</p>
        <p className="mt-1"><Link className="text-brand hover:underline" to={`/e/${ws.id}/details`}>Open engine details</Link> — the step checklist, flow, evidence, planning, areas, completion, client threads, agents, health and release.</p>
      </EngineDetails>
      {drawer}
    </div>
  );
}
